// The link a QR sign points at. It is only encoded in the QR code and printed
// as plain text; it is never fetched, rendered as HTML or sent anywhere.

// Long enough for any gallery or album share link; short enough that the QR
// code on a pocket card keeps modules of about half a millimetre.
export const QR_SIGN_URL_MAX_LENGTH = 300;

export type SignUrlError =
  | "empty"
  | "too-long"
  | "invalid"
  | "scheme"
  | "credentials";

export type SignUrlResult =
  | { ok: true; url: string }
  | { ok: false; reason: SignUrlError };

const SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;
// Controls, spaces, zero-width and bidi characters, plus characters that are
// never valid in a URL. URL parsers silently drop or rewrite these, so the QR
// would no longer match the text printed under it.
const UNSAFE_CODE_POINT_RANGES: Array<[number, number]> = [
  [0x0000, 0x0020],
  [0x007f, 0x00a0],
  [0x00ad, 0x00ad],
  [0x061c, 0x061c],
  [0x115f, 0x1160],
  [0x17b4, 0x17b5],
  [0x180e, 0x180e],
  [0x2000, 0x200f],
  [0x2028, 0x202f],
  [0x205f, 0x206f],
  [0x3000, 0x3000],
  [0x3164, 0x3164],
  [0xfe00, 0xfe0f],
  [0xfeff, 0xfeff],
  [0xffa0, 0xffa0],
  [0xfff0, 0xfff8],
];
const UNSAFE_ASCII = '"<>\\^`{|}';

function hasUnsafeCharacter(value: string): boolean {
  for (const character of Array.from(value)) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (
      UNSAFE_ASCII.indexOf(character) !== -1 ||
      UNSAFE_CODE_POINT_RANGES.some(
        ([from, to]) => codePoint >= from && codePoint <= to
      )
    ) {
      return true;
    }
  }
  return false;
}
const PRINTABLE_ASCII_PATTERN = /^[\x21-\x7e]+$/;

export function parseSignUrl(input: string): SignUrlResult {
  if (input.length > QR_SIGN_URL_MAX_LENGTH * 4) {
    return { ok: false, reason: "too-long" };
  }

  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ok: false, reason: "empty" };
  }
  if (hasUnsafeCharacter(trimmed)) {
    return { ok: false, reason: "invalid" };
  }

  const hasScheme = SCHEME_PATTERN.test(trimmed);
  if (hasScheme && !/^https?:\/\//i.test(trimmed)) {
    return { ok: false, reason: "scheme" };
  }
  if (!hasScheme && trimmed.startsWith("/")) {
    return { ok: false, reason: "invalid" };
  }
  const candidate = hasScheme ? trimmed : `https://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return { ok: false, reason: "invalid" };
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return { ok: false, reason: "scheme" };
  }
  // "https://guest.gallery@evil.example" opens evil.example.
  if (parsed.username || parsed.password || hostPart(candidate).includes("@")) {
    return { ok: false, reason: "credentials" };
  }
  if (!isPublicLookingHost(parsed.hostname)) {
    return { ok: false, reason: "invalid" };
  }

  // Keep exactly what was typed when it is already plain ASCII and parses to
  // the same address; otherwise use the parser's ASCII form (punycode host,
  // percent-encoded path), which is also what gets printed.
  const encoded =
    PRINTABLE_ASCII_PATTERN.test(candidate) && sameAddress(candidate, parsed)
      ? candidate
      : parsed.href;

  if (encoded.length > QR_SIGN_URL_MAX_LENGTH) {
    return { ok: false, reason: "too-long" };
  }
  return { ok: true, url: encoded };
}

function hostPart(url: string): string {
  const afterScheme = url.replace(/^https?:\/\//i, "");
  const end = afterScheme.search(/[/?#]/);
  return end === -1 ? afterScheme : afterScheme.slice(0, end);
}

function isPublicLookingHost(hostname: string): boolean {
  if (!hostname.includes(".") || hostname.startsWith(".")) {
    return false;
  }
  const labels = hostname.replace(/\.$/, "").split(".");
  return labels.length >= 2 && labels.every((label) => label.length > 0);
}

function sameAddress(candidate: string, parsed: URL): boolean {
  const href = parsed.href;
  if (href === candidate) {
    return true;
  }
  // The parser adds "/" to a bare origin and lowercases the scheme and host;
  // phones do the same when they open the link.
  const lower = candidate.replace(
    /^(https?:\/\/[^/?#]*)/i,
    (origin) => origin.toLowerCase()
  );
  return href === lower || href === `${lower}/` || href.replace(/\/(?=[?#]|$)/, "") === lower;
}
